import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Progress,
  Typography,
  Button,
  Space,
  Alert,
  Spin,
} from "antd";
import {
  UserOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  dashboardService,
  testimonialService,
} from "../../../services/index.js";
import { all_routes } from "../../../routes/all_routes";

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, admin: 0 },
    testimonials: { total: 0, pending: 0, approved: 0, rejected: 0 },
    guestLinks: { total: 0, active: 0, used: 0 },
    guestSubmissions: { total: 0, pending: 0 },
    recentActivity: { testimonials: [], guestSubmissions: [] },
  });
  const [recentTestimonials, setRecentTestimonials] = useState([]);
  const [platformOverview, setPlatformOverview] = useState({
    totalViews: 0,
    avgRating: 0,
    approvalRate: 0,
    recentRegistrations: 0,
    totalTestimonials: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const dashboardResponse = await dashboardService.getAdminDashboard();
      if (dashboardResponse.status && dashboardResponse.data) {
        setStats(dashboardResponse.data);
        if (dashboardResponse.data.recentActivity?.testimonials) {
          const formattedTestimonials =
            dashboardResponse.data.recentActivity.testimonials.map((item) => ({
              id: item.id,
              userName: item.displayName || "Unknown User",
              userEmail: item.displayEmail || "",
              title: item.title,
              duration: formatDuration(item.videoDuration),
              status: item.status,
              rating: item.rating || 0,
              submittedAt: item.createdAt,
            }));
          setRecentTestimonials(formattedTestimonials);
        }
      } else {
        setError(dashboardResponse.message || "Failed to load dashboard data");
      }

      // Load platform overview
      const overviewResponse = await dashboardService.getPlatformOverview();
      if (overviewResponse.status && overviewResponse.data) {
        setPlatformOverview(overviewResponse.data);
      } else {
        setError(
          overviewResponse.message || "Failed to load platform overview"
        );
      }
    } catch (error) {
      console.error("Load dashboard error:", error);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleApproveTestimonial = async (testimonialId) => {
    try {
      const response = await testimonialService.updateTestimonialStatus(
        testimonialId,
        "approved"
      );
      if (response.status) {
        loadDashboardData(true);
      } else {
        console.error("Failed to approve testimonial:", response.message);
      }
    } catch (error) {
      console.error("Approve testimonial error:", error);
    }
  };

  const handleViewTestimonial = (testimonialId) => {
    navigate(`${all_routes.adminTestimonials}?view=${testimonialId}`);
  };

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  const columns = [
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
      render: (name, record) => (
        <Space>
          <div>
            <div>{name}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.userEmail}
            </Text>
          </div>
        </Space>
      ),
    },
    // {
    //   title: "Duration",
    //   dataIndex: "duration",
    //   key: "duration",
    //   render: (duration) => <Tag icon={<PlayCircleOutlined />}>{duration}</Tag>,
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          pending: { color: "orange", text: "Pending Review" },
          approved: { color: "green", text: "Approved" },
          rejected: { color: "red", text: "Rejected" },
        };
        const config = statusConfig[status] || {
          color: "default",
          text: status,
        };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      render: (rating) => {
        const numericRating = parseFloat(rating) || 0;
        const roundedRating = Math.round(numericRating * 2) / 2;

        return (
          <Space>
            {Array.from({ length: 5 }, (_, i) => {
              const starValue = i + 1;
              let starIcon = "☆";

              if (roundedRating >= starValue) {
                starIcon = "★";
              } else if (roundedRating >= starValue - 0.5) {
                starIcon = "⭐";
              }

              return (
                <span
                  key={i}
                  style={{
                    color:
                      roundedRating >= starValue - 0.5 ? "#fadb14" : "#d9d9d9",
                    fontSize: "16px",
                  }}
                >
                  {starIcon}
                </span>
              );
            })}
            <Text type="secondary">({numericRating.toFixed(1)})</Text>
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewTestimonial(record.id)}
          >
            View
          </Button>
          {record.status === "pending" && (
            <Button
              type="primary"
              size="small"
              onClick={() => handleApproveTestimonial(record.id)}
            >
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <Title level={2}>Admin Dashboard</Title>
        </div>
        <div className="text-center py-12">
          <Spin size="large" />
          <div className="mt-4">
            <Text>Loading dashboard data...</Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Admin Dashboard</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={refreshing}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="mb-6"
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      )}

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Total Users"
              value={stats.users?.total || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
            <Text type="secondary">
              {stats.users?.active || 0} active, {stats.users?.admin || 0} admin
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Testimonials"
              value={stats.testimonials?.total || 0}
              prefix={<VideoCameraOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
            <Text type="secondary">
              {stats.testimonials?.pending || 0} pending review
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Pending Review"
              value={stats.testimonials?.pending || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
            <Progress
              percent={
                stats.testimonials?.total
                  ? Math.round(
                      ((stats.testimonials.pending || 0) /
                        stats.testimonials.total) *
                        100
                    )
                  : 0
              }
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <Statistic
              title="Active Guest Links"
              value={stats.guestLinks?.active || 0}
              prefix={<LinkOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
            <Text type="secondary">
              {stats.guestSubmissions?.pending || 0} pending submissions
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="Recent Testimonials"
            className="shadow-sm"
            extra={
              <Button
                type="link"
                onClick={() => navigate(all_routes.adminTestimonials)}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={recentTestimonials}
              columns={columns}
              pagination={false}
              size="middle"
              rowKey="id"
              locale={{
                emptyText: "No recent testimonials found",
              }}
            />
          </Card>
        </Col>

        {/* Platform Overview */}
        <Col xs={24} lg={8}>
          <Card title="Platform Overview" className="shadow-sm">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span>Approval Rate</span>
                  <span>{platformOverview.approvalRate || 0}%</span>
                </div>
                <Progress
                  percent={platformOverview.approvalRate || 0}
                  strokeColor="#52c41a"
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span>Average Rating</span>
                  <span>{platformOverview.avgRating || 0}/5</span>
                </div>
                <Progress
                  percent={((platformOverview.avgRating || 0) / 5) * 100}
                  strokeColor="#1890ff"
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span>Total Views</span>
                  <span>{platformOverview.totalViews || 0}</span>
                </div>
                <Text type="secondary" className="block">
                  Across all testimonials
                </Text>
              </div>

              <div>
                <div className="flex justify-between">
                  <span>Recent Registrations</span>
                  <span>{platformOverview.recentRegistrations || 0}</span>
                </div>
                <Text type="secondary" className="block">
                  Last 7 days
                </Text>
              </div>
            </div>
          </Card>

          {/* Quick Stats */}
          <Card title="Quick Stats" className="shadow-sm mt-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <Title level={3} className="text-green-600 mb-0">
                  {stats.testimonials?.approved || 0}
                </Title>
                <Text type="secondary">Approved</Text>
              </div>
              <div>
                <Title level={3} className="text-red-600 mb-0">
                  {stats.testimonials?.rejected || 0}
                </Title>
                <Text type="secondary">Rejected</Text>
              </div>
              <div>
                <Title level={3} className="text-blue-600 mb-0">
                  {stats.guestLinks?.total || 0}
                </Title>
                <Text type="secondary">Total Links</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
